import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `TextField`: cream (or white) fill, brown hairline, --r-sm
 * radius, orange border + 2px orange ring on focus, danger border when
 * invalid. Forwards its ref to the <input> so react-hook-form can register it.
 */
export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  surface?: 'cream' | 'white';
  leading?: ReactNode;
  trailing?: ReactNode;
  containerClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { invalid = false, surface = 'cream', leading, trailing, disabled, readOnly, className, containerClassName, ...rest },
  ref,
) {
  const muted = disabled || readOnly;
  return (
    <div
      className={cx(
        'flex items-center gap-2 rounded-input border px-3.5 transition-[border-color] duration-fast',
        'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-orange',
        muted ? 'bg-[var(--hp-gray-100)]' : surface === 'white' ? 'bg-white' : 'bg-cream',
        invalid ? 'border-danger' : 'border-[rgba(143,68,32,.12)] focus-within:border-orange',
        containerClassName,
      )}
    >
      {leading && <span className="flex text-muted">{leading}</span>}
      <input
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={invalid || undefined}
        className={cx(
          'min-w-0 flex-1 border-0 bg-transparent py-[13px] font-body text-[15px] outline-none',
          'focus-visible:outline-none',
          muted ? 'text-muted' : 'text-ink',
          className,
        )}
        {...rest}
      />
      {trailing && <span className="flex text-muted">{trailing}</span>}
    </div>
  );
});
